import { Link } from '@tanstack/react-router';
import { Link2, Search } from 'lucide-react';
import { useAuth } from '../../context/auth-context';
import { useAdminClaim } from '../../hooks/use-admin-claim';
import { useTopbarSearch } from '../../hooks/use-topbar-search';
import { TopBarProfileMenu } from './top-bar-profile-menu';
import { TopBarSearchDropdown } from './top-bar-search-dropdown';
import { useTopBarMenu } from './top-bar-menu-context';

const ADMIN_EMAILS = new Set(['frode@vekseth.com', 'frodevek@gmail.com']);

export function TopBar() {
  const { user, signOut } = useAuth();
  const { data: hasAdminClaim } = useAdminClaim();
  const { caseMenuActions } = useTopBarMenu();
  const search = useTopbarSearch();
  const userEmail = user?.email?.toLowerCase();
  const showAdminLink = hasAdminClaim === true || (userEmail ? ADMIN_EMAILS.has(userEmail) : false);

  return (
    <header
      className="sticky top-0 z-30 h-12 border-b px-4"
      style={{
        background: 'rgba(17,18,24,0.92)',
        backdropFilter: 'blur(12px)',
        borderColor: 'rgba(255,255,255,0.07)',
      }}
    >
      <Link
        to="/cases"
        className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold tracking-tight text-text-primary hover:text-white"
      >
        CaseTrace
      </Link>

      <div
        ref={search.containerRef}
        className="absolute left-1/2 top-1/2 w-[min(28rem,calc(100vw-13rem))] -translate-x-1/2 -translate-y-1/2"
      >
        <div
          className="flex w-full items-center gap-2 rounded-md border px-3 py-1.5 transition-colors focus-within:border-white/25 focus-within:bg-white/[0.05]"
          style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}
        >
          <Search size={13} className="shrink-0 text-text-secondary" />
          <input
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            onFocus={search.onFocus}
            onKeyDown={search.onKeyDown}
            placeholder="Søk i saker, hendelser, dokumenter…"
            aria-label="Søk"
            aria-expanded={search.showDropdown}
            aria-autocomplete="list"
            role="combobox"
            autoComplete="off"
            spellCheck={false}
            className="min-w-0 flex-1 bg-transparent text-xs text-text-primary outline-none placeholder:text-text-secondary"
          />
          <kbd
            className="hidden shrink-0 rounded border px-1 py-0.5 text-[10px] text-text-secondary sm:block"
            style={{ borderColor: 'rgba(255,255,255,0.1)' }}
          >
            ⌘K
          </kbd>
        </div>

        {search.showDropdown && (
          <TopBarSearchDropdown
            results={search.results}
            activeIndex={search.activeIndex}
            isSearching={search.isSearching}
            isConfigured={search.isConfigured}
            onSelect={search.selectResult}
            onHover={search.setActiveIndex}
          />
        )}
      </div>

      <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {caseMenuActions && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-text-secondary transition-colors hover:bg-white/6 hover:text-text-primary"
            onClick={caseMenuActions.onOpenSources}
          >
            <Link2 size={14} />
            <span>Hent data</span>
          </button>
        )}
        <TopBarProfileMenu
          triggerLabel="Meny"
          userEmail={user?.email ?? null}
          showAdminLink={showAdminLink}
          caseMenuActions={caseMenuActions}
          onSignOut={() => void signOut()}
        />
      </div>
    </header>
  );
}
